import { useNavigate, useParams, Link } from "react-router-dom";

import { Space, Breadcrumb, Button, Card, Modal, Select, Typography, Alert, Tag } from "antd";
import { PageHeader } from "@ant-design/pro-layout";

import { GetDeviceProfileRequest } from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import { ListTenantsRequest } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";
import {
  DeviceProfileDevice,
  DeviceProfileVendor,
  GetDeviceProfileResponse,
  DeviceProfile,
  DeleteDeviceProfileRequest,
  CreateDeviceProfileRequest,
  UpdateDeviceProfileRequest,
  ListDeviceProfileVendorsRequest,
  ListDeviceProfileDevicesRequest,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import type {
  CreateDeviceProfileResponse,
  ListDeviceProfileVendorsResponse,
  ListDeviceProfileDevicesResponse,
} from "@chirpstack/chirpstack-api-grpc-web/api/device_profile_pb";
import type { ListTenantsResponse } from "@chirpstack/chirpstack-api-grpc-web/api/tenant_pb";

import { useTitle } from "../helpers";
import { useEffect, useState } from "react";
import DeviceProfileStore from "../../stores/DeviceProfileStore";
import DeviceProfileForm from "./DeviceProfileForm";
import DeleteConfirm from "../../components/DeleteConfirm";
import TenantStore from "../../stores/TenantStore";

interface SelectOption {
  value: string;
  label: string;
}

interface IProps {
  vendor: DeviceProfileVendor;
  device: DeviceProfileDevice;
}

function ShowDeviceProfile(props: IProps) {
  const navigate = useNavigate();
  const [deviceProfile, setDeviceProfile] = useState<DeviceProfile | undefined>(undefined);
  const { deviceProfileId } = useParams();

  // Copy-to-tenant modal state.
  const [copyTenantModalOpen, setCopyTenantModalOpen] = useState<boolean>(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [tenantOptions, setTenantOptions] = useState<SelectOption[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState<boolean>(false);

  // Copy-to-network-server modal state.
  const [copyNsModalOpen, setCopyNsModalOpen] = useState<boolean>(false);
  const [vendorOptions, setVendorOptions] = useState<SelectOption[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState<boolean>(false);
  const [nsSelectedVendorId, setNsSelectedVendorId] = useState<string>("");
  const [deviceOptions, setDeviceOptions] = useState<SelectOption[]>([]);
  const [devicesLoading, setDevicesLoading] = useState<boolean>(false);
  const [nsSelectedDeviceId, setNsSelectedDeviceId] = useState<string>("");

  useTitle([
    "Network Server",
    "Device Profiles",
    "Vendors",
    props.vendor.getName(),
    props.device.getName(),
    deviceProfile?.getName(),
  ]);

  useEffect(() => {
    const req = new GetDeviceProfileRequest();
    req.setId(deviceProfileId!);

    DeviceProfileStore.get(req, (resp: GetDeviceProfileResponse) => {
      setDeviceProfile(resp.getDeviceProfile());
    });
  }, [deviceProfileId]);

  useEffect(() => {
    setTenantsLoading(true);
    const req = new ListTenantsRequest();
    req.setLimit(1000);

    TenantStore.list(req, (resp: ListTenantsResponse) => {
      setTenantOptions(
        resp.getResultList().map(v => ({
          value: v.getId(),
          label: v.getName(),
        })),
      );
      setTenantsLoading(false);
    });
  }, []);

  // Lazy-load vendor list when the user opens the copy-to-NS modal.
  useEffect(() => {
    if (!copyNsModalOpen || vendorOptions.length > 0) {
      return;
    }
    setVendorsLoading(true);
    const req = new ListDeviceProfileVendorsRequest();
    req.setLimit(1000);
    DeviceProfileStore.listVendors(req, (resp: ListDeviceProfileVendorsResponse) => {
      setVendorOptions(resp.getResultList().map(v => ({ value: v.getId(), label: v.getName() })));
      setVendorsLoading(false);
    });
  }, [copyNsModalOpen, vendorOptions.length]);

  // Reload devices whenever the user picks a different vendor in the modal.
  useEffect(() => {
    if (nsSelectedVendorId === "") {
      setDeviceOptions([]);
      setNsSelectedDeviceId("");
      return;
    }
    setDevicesLoading(true);
    setNsSelectedDeviceId("");
    const req = new ListDeviceProfileDevicesRequest();
    req.setVendorId(nsSelectedVendorId);
    req.setLimit(1000);
    DeviceProfileStore.listDevices(req, (resp: ListDeviceProfileDevicesResponse) => {
      setDeviceOptions(resp.getResultList().map(d => ({ value: d.getId(), label: d.getName() })));
      setDevicesLoading(false);
    });
  }, [nsSelectedVendorId]);

  const deleteDeviceProfile = () => {
    const req = new DeleteDeviceProfileRequest();
    req.setId(deviceProfile!.getId());

    DeviceProfileStore.delete(req, () => {
      navigate(`/device-profiles/vendors/${props.vendor.getId()}/devices/${props.device.getId()}/profiles`);
    });
  };

  if (!deviceProfile) {
    return null;
  }

  const isBridge = deviceProfile.getSource() === "bridge";

  // In-place edit: when source='bridge' the form is editable and Submit
  // routes to Update. Repo profiles stay disabled (you can only copy them
  // to a tenant or to a new bridge template via the buttons above).
  const onFinishEdit = (dp: DeviceProfile) => {
    dp.setId(deviceProfile.getId());
    // Preserve template identity — tenant_id stays empty, device_id stays
    // bound to the current device. The server enforces this anyway via
    // the source != 'bridge' guard, but explicit is cheap.
    dp.setTenantId("");
    dp.setDeviceId(props.device.getId());

    const req = new UpdateDeviceProfileRequest();
    req.setDeviceProfile(dp);

    DeviceProfileStore.update(req, () => {
      // Refresh the loaded profile so the form re-binds with persisted values.
      const getReq = new GetDeviceProfileRequest();
      getReq.setId(deviceProfile.getId());
      DeviceProfileStore.get(getReq, (resp: GetDeviceProfileResponse) => {
        setDeviceProfile(resp.getDeviceProfile());
      });
    });
  };

  const copyToTenant = () => {
    if (selectedTenantId === "") {
      return;
    }

    const dp = deviceProfile.clone();
    dp.setId("");
    dp.setDeviceId("");
    dp.setTenantId(selectedTenantId);

    const req = new CreateDeviceProfileRequest();
    req.setDeviceProfile(dp);

    DeviceProfileStore.create(req, (resp: CreateDeviceProfileResponse) => {
      setCopyTenantModalOpen(false);
      navigate(`/tenants/${selectedTenantId}/device-profiles/${resp.getId()}/edit`);
    });
  };

  const copyToNs = () => {
    if (nsSelectedVendorId === "" || nsSelectedDeviceId === "") {
      return;
    }

    const dp = deviceProfile.clone();
    dp.setId("");
    dp.setTenantId("");
    dp.setDeviceId(nsSelectedDeviceId);
    // Source is server-set on Create (always 'bridge' for new templates).

    const req = new CreateDeviceProfileRequest();
    req.setDeviceProfile(dp);

    DeviceProfileStore.create(req, (resp: CreateDeviceProfileResponse) => {
      setCopyNsModalOpen(false);
      navigate(
        `/device-profiles/vendors/${nsSelectedVendorId}/devices/${nsSelectedDeviceId}/profiles/${resp.getId()}`,
      );
    });
  };

  return (
    <Space orientation="vertical" style={{ width: "100%" }} size="large">
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb
            items={[
              { title: "Network Server" },
              { title: "Device Profiles" },
              { title: <Link to={`/device-profiles/vendors`}>Vendors</Link> },
              {
                title: (
                  <Link to={`/device-profiles/vendors/${props.vendor.getId()}/devices`}>{props.vendor.getName()}</Link>
                ),
              },
              {
                title: (
                  <Link
                    to={`/device-profiles/vendors/${props.vendor.getId()}/devices/${props.device.getId()}/profiles`}
                  >
                    {props.device.getName()}
                  </Link>
                ),
              },
              { title: deviceProfile.getName() },
            ]}
          />
        )}
        title={deviceProfile.getName()}
        tags={
          deviceProfile.getSource() === "repo" ? <Tag color="blue">Repo</Tag> : <Tag color="green">Bridge</Tag>
        }
        extra={[
          <Button
            type="primary"
            key="copy-device-profile-tenant"
            onClick={() => {
              setSelectedTenantId("");
              setCopyTenantModalOpen(true);
            }}
          >
            Copy to tenant
          </Button>,
          <Button
            key="copy-device-profile-ns"
            onClick={() => {
              setNsSelectedVendorId("");
              setNsSelectedDeviceId("");
              setCopyNsModalOpen(true);
            }}
          >
            Copy to network server
          </Button>,
          <DeleteConfirm
            typ="device profile"
            confirm={deviceProfile.getName()}
            onConfirm={deleteDeviceProfile}
            key="delete-device-profile"
          >
            <Button danger type="primary">
              Delete device profile
            </Button>
          </DeleteConfirm>,
        ]}
      />
      <Card>
        {isBridge ? (
          <DeviceProfileForm initialValues={deviceProfile} onFinish={onFinishEdit} isTemplate />
        ) : (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="This device profile was imported from the device-profiles repo and is read-only."
              description="Use 'Copy to network server' to create an editable copy under a vendor/device of your choice, or 'Copy to tenant' to assign it to a tenant."
            />
            <DeviceProfileForm initialValues={deviceProfile} disabled onFinish={() => {}} isTemplate />
          </>
        )}
      </Card>

      {/* Copy-to-tenant modal */}
      <Modal
        title="Copy to tenant"
        open={copyTenantModalOpen}
        onCancel={() => setCopyTenantModalOpen(false)}
        onOk={copyToTenant}
        okText="Copy profile"
        okButtonProps={{ disabled: selectedTenantId === "" || tenantOptions.length === 0 }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Typography.Text>Select destination tenant</Typography.Text>
          <Select
            placeholder="Select tenant"
            loading={tenantsLoading}
            options={tenantOptions}
            value={selectedTenantId || undefined}
            onChange={setSelectedTenantId}
            showSearch
            optionFilterProp="label"
          />
          <Typography.Text type="secondary">
            The copied profile will be editable only in that tenant context.
          </Typography.Text>
          {tenantOptions.length === 0 && !tenantsLoading && (
            <Alert
              type="warning"
              message="No tenants available"
              description="Create a tenant first, then copy this profile."
              showIcon
            />
          )}
        </Space>
      </Modal>

      {/* Copy-to-network-server modal */}
      <Modal
        title="Copy to network server"
        open={copyNsModalOpen}
        onCancel={() => setCopyNsModalOpen(false)}
        onOk={copyToNs}
        okText="Copy profile"
        okButtonProps={{ disabled: nsSelectedVendorId === "" || nsSelectedDeviceId === "" }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Typography.Text>Select destination vendor</Typography.Text>
          <Select
            placeholder="Select vendor"
            loading={vendorsLoading}
            options={vendorOptions}
            value={nsSelectedVendorId || undefined}
            onChange={setNsSelectedVendorId}
            showSearch
            optionFilterProp="label"
          />
          <Typography.Text>Select destination device</Typography.Text>
          <Select
            placeholder={nsSelectedVendorId === "" ? "Select a vendor first" : "Select device"}
            loading={devicesLoading}
            options={deviceOptions}
            value={nsSelectedDeviceId || undefined}
            onChange={setNsSelectedDeviceId}
            disabled={nsSelectedVendorId === ""}
            showSearch
            optionFilterProp="label"
          />
          <Typography.Text type="secondary">
            A new <strong>Bridge</strong> device profile will be created under the selected vendor and device. You can
            edit it freely afterward.
          </Typography.Text>
          {nsSelectedVendorId !== "" && deviceOptions.length === 0 && !devicesLoading && (
            <Alert
              type="warning"
              message="No devices under this vendor"
              description="Add a device under this vendor first, then copy this profile."
              showIcon
            />
          )}
        </Space>
      </Modal>
    </Space>
  );
}

export default ShowDeviceProfile;
